import { useState, useEffect } from 'react';
import './App.css';
import { Board } from './components/Board';
import { MoveHistory } from './components/MoveHistory';
import { MaterialBalance } from './components/MaterialBalance';
import { CapturedPieces } from './components/CapturedPieces';
import { EvaluationBar } from './components/EvaluationBar';
import { PGNDialog } from './components/PGNDialog';
import { SettingsPanel } from './components/SettingsPanel';
import { OpeningExplorer } from './components/OpeningExplorer';
import { createInitialGameState, parseFEN } from '../engine/board';
import { isInCheck, makeMove } from '../engine/moves';
import { 
  isCheckmate, 
  isStalemate, 
  isInsufficientMaterial, 
  isFiftyMoveRule, 
  isThreefoldRepetition,
  getGameResult 
} from '../engine/endgame';
import { moveToAlgebraic, AnnotatedMove } from '../engine/notation';
import { exportToPGN, importFromPGN, loadPGN } from '../engine/pgn';
import { GameState, Color } from '../engine/types';
import { findBestMove } from '../engine/search';
import { soundManager } from './utils/sounds';
import { themeManager, UITheme, BoardTheme } from './utils/themes';
import { pieceSetManager, PieceSet } from './utils/pieceSets';

/**
 * Main App component - chess game UI
 */
function App() {
  // Position history for undo/redo functionality
  const [positionHistory, setPositionHistory] = useState<GameState[]>([createInitialGameState()]);
  const [currentPositionIndex, setCurrentPositionIndex] = useState(0);
  const [moveHistory, setMoveHistory] = useState<AnnotatedMove[]>([]);
  const [flipped, setFlipped] = useState(false);
  
  // Game mode: 'human-vs-human', 'human-vs-ai', 'ai-vs-human', 'ai-vs-ai'
  type GameMode = 'human-vs-human' | 'human-vs-ai' | 'ai-vs-human' | 'ai-vs-ai';
  const [gameMode, setGameMode] = useState<GameMode>('human-vs-human');
  
  // Difficulty level (search depth)
  const [difficulty, setDifficulty] = useState<number>(3); // 1=Easy, 3=Medium, 5=Hard (for single-AI modes)
  
  // Separate difficulties for AI vs AI mode
  const [whiteDifficulty, setWhiteDifficulty] = useState<number>(3);
  const [blackDifficulty, setBlackDifficulty] = useState<number>(3);
  
  // AI thinking state
  const [aiThinking, setAiThinking] = useState(false);
  
  // PGN dialog state
  const [pgnDialogMode, setPgnDialogMode] = useState<'export' | 'import' | null>(null);
  const [exportedPGN, setExportedPGN] = useState('');
  
  // Sound state
  const [soundsEnabled, setSoundsEnabled] = useState(soundManager.isEnabled());
  const [soundVolume, setSoundVolume] = useState(soundManager.getVolume());
  
  // AI vs AI playback speed (1.0 = normal, 0.5 = half speed, 2.0 = double speed)
  const [aiVsAiSpeed, setAiVsAiSpeed] = useState<number>(1.0);
  
  // Move hint state
  const [hintMove, setHintMove] = useState<any>(null); // Move | null
  const [calculatingHint, setCalculatingHint] = useState(false);
  
  // Theme state
  const [uiTheme, setUITheme] = useState<UITheme>(themeManager.getUITheme());
  const [boardTheme, setBoardTheme] = useState<BoardTheme>(themeManager.getBoardTheme());
  
  // Piece set state
  const [pieceSet, setPieceSet] = useState<PieceSet>(pieceSetManager.getPieceSet());
  
  // Settings panel state
  const [settingsPanelOpen, setSettingsPanelOpen] = useState(false);
  
  // Current game state is the position at currentPositionIndex
  const gameState = positionHistory[currentPositionIndex];
  
  // Last move is the move that led to current position (or null if at start)
  const lastMove = currentPositionIndex > 0 ? moveHistory[currentPositionIndex - 1]?.move : null;

  // Calculate game status
  const inCheck = isInCheck(gameState, gameState.activeColor);
  const checkmated = isCheckmate(gameState);
  const stalemated = isStalemate(gameState);
  const insufficientMaterial = isInsufficientMaterial(gameState);
  const fiftyMoveRule = isFiftyMoveRule(gameState);
  const threefoldRepetition = isThreefoldRepetition(gameState);
  const gameResult = getGameResult(gameState);
  const gameOver = gameResult !== null;

  // Check if we're viewing a position in the past
  const isViewingHistory = currentPositionIndex < positionHistory.length - 1;

  // Determine what status message to show
  let statusMessage = '';
  let statusClass = '';
  
  if (checkmated) {
    const winner = gameState.activeColor === 0 ? 'Black' : 'White';
    statusMessage = `Checkmate! ${winner} wins!`;
    statusClass = 'status-checkmate';
  } else if (stalemated) {
    statusMessage = 'Stalemate - Draw!';
    statusClass = 'status-stalemate';
  } else if (insufficientMaterial) {
    statusMessage = 'Draw - Insufficient material';
    statusClass = 'status-draw';
  } else if (fiftyMoveRule) {
    statusMessage = 'Draw - Fifty-move rule';
    statusClass = 'status-draw';
  } else if (threefoldRepetition) {
    statusMessage = 'Draw - Threefold repetition';
    statusClass = 'status-draw';
  } else if (inCheck) {
    statusMessage = 'Check!';
    statusClass = 'status-check';
  }

  const handleNewGame = () => {
    const initialState = createInitialGameState();
    setPositionHistory([initialState]);
    setCurrentPositionIndex(0);
    setMoveHistory([]);
    setAiThinking(false);
  };

  // Execute a move programmatically (for AI)
  const executeMove = (newState: GameState, move: any) => {
    // Clear hint when a move is made
    setHintMove(null);
    
    // If viewing history, truncate future moves
    const newPositionHistory = positionHistory.slice(0, currentPositionIndex + 1);
    const newMoveHistory = moveHistory.slice(0, currentPositionIndex);
    
    // Convert move to algebraic notation
    const notation = moveToAlgebraic(move, gameState, newState);
    
    // Play appropriate sound effect
    if (move.isPromotion) {
      soundManager.play('promote');
    } else if (move.isCastling) {
      soundManager.play('castle');
    } else if (move.capturedPiece !== undefined) {
      soundManager.play('capture');
    } else {
      soundManager.play('move');
    }
    
    // Check for check/checkmate/draw after move
    const newInCheck = isInCheck(newState, newState.activeColor);
    const newCheckmated = isCheckmate(newState);
    const newDrawn = isStalemate(newState) || isInsufficientMaterial(newState) || 
                     isFiftyMoveRule(newState) || isThreefoldRepetition(newState);
    
    // Play sound for game state (with slight delay so it doesn't overlap with move sound)
    setTimeout(() => {
      if (newCheckmated) {
        soundManager.play('checkmate');
      } else if (newDrawn) {
        soundManager.play('draw');
      } else if (newInCheck) {
        soundManager.play('check');
      }
    }, 100);
    
    // Add to histories
    setPositionHistory([...newPositionHistory, newState]);
    setMoveHistory([...newMoveHistory, { move, notation }]);
    setCurrentPositionIndex(newPositionHistory.length);
  };

  // AI move logic
  useEffect(() => {
    // Don't make AI moves if:
    // - Game is over
    // - Viewing history (not at latest position)
    // - AI is already thinking
    if (gameOver || isViewingHistory || aiThinking) {
      return;
    }

    // Determine if it's AI's turn
    const isAiTurn = 
      (gameMode === 'human-vs-ai' && gameState.activeColor === Color.Black) ||
      (gameMode === 'ai-vs-human' && gameState.activeColor === Color.White) ||
      (gameMode === 'ai-vs-ai'); // Both players are AI

    if (isAiTurn) {
      setAiThinking(true);

      // Use setTimeout to allow UI to update (show thinking indicator)
      // In AI vs AI mode, add extra delay so moves are visible (with speed control)
      const baseDelay = gameMode === 'ai-vs-ai' ? 500 : 100;
      const delay = gameMode === 'ai-vs-ai' ? baseDelay / aiVsAiSpeed : baseDelay;
      setTimeout(() => {
        // Determine which difficulty to use
        let searchDepth = difficulty;
        if (gameMode === 'ai-vs-ai') {
          searchDepth = gameState.activeColor === Color.White ? whiteDifficulty : blackDifficulty;
        }
        
        const bestMove = findBestMove(gameState, searchDepth);
        
        if (bestMove) {
          const newState = makeMove(gameState, bestMove);
          executeMove(newState, bestMove);
        }
        
        setAiThinking(false);
      }, delay);
    }
  }, [gameState, gameMode, difficulty, whiteDifficulty, blackDifficulty, gameOver, isViewingHistory, aiThinking, currentPositionIndex, positionHistory, moveHistory, aiVsAiSpeed]);

  // Update volume slider CSS custom property for visual feedback
  useEffect(() => {
    const sliders = document.querySelectorAll('.volume-slider');
    sliders.forEach((slider) => {
      (slider as HTMLElement).style.setProperty('--slider-value', `${soundVolume * 100}%`);
    });
  }, [soundVolume]);

  const handleGameModeChange = (mode: GameMode) => {
    setGameMode(mode);
    setAiThinking(false);
  };

  const handleDifficultyChange = (depth: number) => {
    setDifficulty(depth);
  };
  
  const handleWhiteDifficultyChange = (depth: number) => {
    setWhiteDifficulty(depth);
  };
  
  const handleBlackDifficultyChange = (depth: number) => {
    setBlackDifficulty(depth);
  };

  const handleFlipBoard = () => {
    setFlipped(prev => !prev);
  };

  const handleUndo = () => {
    if (currentPositionIndex > 0) {
      setCurrentPositionIndex(currentPositionIndex - 1);
    }
  };

  const handleRedo = () => {
    if (currentPositionIndex < positionHistory.length - 1) {
      setCurrentPositionIndex(currentPositionIndex + 1);
    }
  };

  const handleGoToStart = () => {
    setCurrentPositionIndex(0);
  };

  const handleGoToEnd = () => {
    setCurrentPositionIndex(positionHistory.length - 1);
  };

  const handleGoToMove = (moveIndex: number) => {
    // moveIndex is 0-based index in moveHistory
    // Position index is moveIndex + 1 (since position 0 is starting position)
    setCurrentPositionIndex(moveIndex + 1);
  };

  /**
   * Handle export game to PGN
   */
  const handleExportPGN = () => {
    // Get initial FEN (from position 0)
    const initialFEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
    
    // Determine result based on game state
    let result = '*'; // In progress
    if (gameOver) {
      if (checkmated) {
        result = gameState.activeColor === Color.White ? '0-1' : '1-0';
      } else {
        result = '1/2-1/2'; // Draw
      }
    }
    
    // Export to PGN
    const pgn = exportToPGN(moveHistory, {
      Event: 'Casual Game',
      Site: 'Chesses React',
      Date: new Date().toISOString().split('T')[0].replace(/-/g, '.'),
      White: gameMode === 'ai-vs-human' ? 'AI' : 'Player 1',
      Black: gameMode === 'human-vs-ai' ? 'AI' : gameMode === 'ai-vs-ai' ? 'AI' : 'Player 2',
      Result: result,
    }, initialFEN);
    
    setExportedPGN(pgn);
    setPgnDialogMode('export');
  };

  /**
   * Handle import game from PGN
   */
  const handleImportPGN = (pgnText: string) => {
    try {
      const pgnGame = importFromPGN(pgnText);
      
      if (!pgnGame) {
        alert('Failed to parse PGN. Please check the format and try again.');
        return;
      }
      
      // Get initial state
      const initialFEN = pgnGame.initialFEN || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
      const initialState = parseFEN(initialFEN);
      
      if (!initialState) {
        alert('Failed to parse starting position from PGN.');
        return;
      }
      
      // Load the game
      const { positionHistory: newPositionHistory, moveHistory: newMoveHistory } = loadPGN(pgnGame, initialState);
      
      // Update state
      setPositionHistory(newPositionHistory);
      setMoveHistory(newMoveHistory);
      setCurrentPositionIndex(newPositionHistory.length - 1);
      setPgnDialogMode(null);
      setAiThinking(false);
      
      console.log(`Loaded PGN game with ${newMoveHistory.length} moves`);
    } catch (error) {
      console.error('Error importing PGN:', error);
      alert('Failed to import PGN. Please check the format and try again.');
    }
  };

  /**
   * Close PGN dialog
   */
  const handleClosePGNDialog = () => {
    setPgnDialogMode(null);
  };

  /**
   * Toggle sound effects on/off
   */
  const handleToggleSound = () => {
    const newState = soundManager.toggle();
    setSoundsEnabled(newState);
  };

  /**
   * Change AI vs AI playback speed
   */
  const handleSpeedChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newSpeed = parseFloat(event.target.value);
    setAiVsAiSpeed(newSpeed);
  };

  /**
   * Show hint - calculate best move for current player
   */
  const handleShowHint = async () => {
    setCalculatingHint(true);
    setHintMove(null);
    
    // Use setTimeout to allow UI to update with calculating message
    setTimeout(() => {
      // Use appropriate difficulty based on whose turn it is in AI vs AI mode
      let searchDepth = difficulty;
      if (gameMode === 'ai-vs-ai') {
        searchDepth = gameState.activeColor === Color.White ? whiteDifficulty : blackDifficulty;
      }
      
      const bestMove = findBestMove(gameState, searchDepth);
      setHintMove(bestMove);
      setCalculatingHint(false);
    }, 50);
  };

  /**
   * Clear hint
   */
  const handleClearHint = () => {
    setHintMove(null);
  };
  
  /**
   * Open settings panel
   */
  const handleOpenSettings = () => {
    setSettingsPanelOpen(true);
  };
  
  /**
   * Close settings panel
   */
  const handleCloseSettings = () => {
    setSettingsPanelOpen(false);
  };
  
  /**
   * Change UI theme (light/dark)
   */
  const handleUIThemeChange = (theme: UITheme) => {
    themeManager.setUITheme(theme);
    setUITheme(theme);
  };
  
  /**
   * Change board theme (color scheme)
   */
  const handleBoardThemeChange = (theme: BoardTheme) => {
    themeManager.setBoardTheme(theme);
    setBoardTheme(theme);
  };

  /**
   * Change piece set
   */
  const handlePieceSetChange = (set: PieceSet) => {
    pieceSetManager.setPieceSet(set);
    setPieceSet(set);
  };

  return (
    <div className="app">
      <h1>Chesses React</h1>
      
      <div className="game-layout">
        <div className="board-section">
          {statusMessage && (
            <div className={`game-status ${statusClass}`}>
              {statusMessage}
            </div>
          )}
          
          {isViewingHistory && (
            <div className="history-indicator">
              Viewing move {currentPositionIndex} of {positionHistory.length - 1}
            </div>
          )}
          
          <div className="turn-indicator">
            <strong>Turn:</strong> {gameState.activeColor === 0 ? 'White' : 'Black'}
          </div>
          
          {aiThinking && (
            <div className="ai-thinking">
              🤔 AI is thinking...
            </div>
          )}
          
          <div className="game-mode-controls">
            <div className="control-group">
              <label>Game Mode:</label>
              <select 
                value={gameMode} 
                onChange={(e) => handleGameModeChange(e.target.value as GameMode)}
                disabled={aiThinking}
              >
                <option value="human-vs-human">Human vs Human</option>
                <option value="human-vs-ai">Human (White) vs AI (Black)</option>
                <option value="ai-vs-human">AI (White) vs Human (Black)</option>
                <option value="ai-vs-ai">AI vs AI</option>
              </select>
            </div>
            
            {gameMode !== 'human-vs-human' && gameMode !== 'ai-vs-ai' && (
              <div className="control-group">
                <label>AI Difficulty:</label>
                <select 
                  value={difficulty} 
                  onChange={(e) => handleDifficultyChange(parseInt(e.target.value))}
                  disabled={aiThinking}
                >
                  <option value="2">Easy (Depth 2)</option>
                  <option value="3">Medium (Depth 3)</option>
                  <option value="4">Hard (Depth 4)</option>
                  <option value="5">Very Hard (Depth 5)</option>
                </select>
              </div>
            )}
            
            {gameMode === 'ai-vs-ai' && (
              <>
                <div className="control-group">
                  <label>White AI:</label>
                  <select 
                    value={whiteDifficulty} 
                    onChange={(e) => handleWhiteDifficultyChange(parseInt(e.target.value))}
                    disabled={aiThinking}
                    title="Difficulty level for White AI"
                  >
                    <option value="2">Easy (Depth 2)</option>
                    <option value="3">Medium (Depth 3)</option>
                    <option value="4">Hard (Depth 4)</option>
                    <option value="5">Very Hard (Depth 5)</option>
                  </select>
                </div>
                <div className="control-group">
                  <label>Black AI:</label>
                  <select 
                    value={blackDifficulty} 
                    onChange={(e) => handleBlackDifficultyChange(parseInt(e.target.value))}
                    disabled={aiThinking}
                    title="Difficulty level for Black AI"
                  >
                    <option value="2">Easy (Depth 2)</option>
                    <option value="3">Medium (Depth 3)</option>
                    <option value="4">Hard (Depth 4)</option>
                    <option value="5">Very Hard (Depth 5)</option>
                  </select>
                </div>
              </>
            )}
            
            {gameMode === 'ai-vs-ai' && (
              <div className="control-group">
                <label>Playback Speed:</label>
                <select 
                  value={aiVsAiSpeed} 
                  onChange={handleSpeedChange}
                  disabled={aiThinking}
                  title="Control how fast AI vs AI moves are played"
                >
                  <option value="0.5">0.5× (Slow)</option>
                  <option value="1">1× (Normal)</option>
                  <option value="2">2× (Fast)</option>
                  <option value="4">4× (Very Fast)</option>
                </select>
              </div>
            )}
          </div>
          
          <Board 
            gameState={gameState} 
            onMove={(newState, move) => {
              executeMove(newState, move);
            }} 
            lastMove={lastMove}
            disabled={gameOver || aiThinking || isViewingHistory}
            flipped={flipped}
            hintMove={hintMove}
            pieceSet={pieceSet}
          />
          
          <div className="game-controls">
            <button onClick={handleNewGame} className="new-game-button">
              New Game
            </button>
            <button onClick={handleFlipBoard} className="flip-board-button">
              Flip Board
            </button>
            <button 
              onClick={handleExportPGN} 
              className="pgn-button-control"
              disabled={moveHistory.length === 0}
              title="Save game as PGN"
            >
              💾 Save PGN
            </button>
            <button 
              onClick={() => setPgnDialogMode('import')} 
              className="pgn-button-control"
              title="Load game from PGN"
            >
              📂 Load PGN
            </button>
            <button 
              onClick={handleOpenSettings} 
              className="settings-button-control"
              title="Open settings"
            >
              ⚙️ Settings
            </button>
          </div>
          
          <div className="navigation-controls">
            <button 
              onClick={handleGoToStart} 
              disabled={currentPositionIndex === 0}
              className="nav-button"
              title="Go to start"
            >
              ⏮
            </button>
            <button 
              onClick={handleUndo} 
              disabled={currentPositionIndex === 0}
              className="nav-button"
              title="Previous move"
            >
              ◀
            </button>
            <button 
              onClick={handleRedo} 
              disabled={currentPositionIndex >= positionHistory.length - 1}
              className="nav-button"
              title="Next move"
            >
              ▶
            </button>
            <button 
              onClick={handleGoToEnd} 
              disabled={currentPositionIndex >= positionHistory.length - 1}
              className="nav-button"
              title="Go to end"
            >
              ⏭
            </button>
          </div>
          
          <div className="hint-controls">
            {calculatingHint && (
              <div className="hint-message calculating">
                💭 Calculating best move...
              </div>
            )}
            {hintMove && !calculatingHint && (
              <div className="hint-message active">
                💡 Hint shown on board - best move highlighted
              </div>
            )}
            <button 
              onClick={hintMove ? handleClearHint : handleShowHint} 
              disabled={
                gameOver || 
                aiThinking || 
                isViewingHistory || 
                gameMode === 'ai-vs-ai' ||
                calculatingHint
              }
              className={`hint-button ${hintMove ? 'clear-hint' : 'show-hint'}`}
              title={hintMove ? "Clear hint" : "Show best move for current player"}
            >
              {calculatingHint ? '💭 Calculating...' : hintMove ? '✖️ Clear Hint' : '💡 Show Hint'}
            </button>
          </div>
          
          <div className="game-info">
            <div>Halfmove Clock: {gameState.halfmoveClock}</div>
            <div>Fullmove Number: {gameState.fullmoveNumber}</div>
          </div>
        </div>
        
        <MaterialBalance gameState={gameState} />
        
        <EvaluationBar gameState={gameState} />
        
        <CapturedPieces gameState={gameState} />
        
        <OpeningExplorer gameState={gameState} />
        
        <MoveHistory 
          moves={moveHistory} 
          onMoveClick={handleGoToMove}
          currentMoveIndex={currentPositionIndex - 1}
        />
      </div>
      
      {/* PGN Dialog */}
      {pgnDialogMode && (
        <PGNDialog
          mode={pgnDialogMode}
          pgnText={pgnDialogMode === 'export' ? exportedPGN : ''}
          onClose={handleClosePGNDialog}
          onImport={pgnDialogMode === 'import' ? handleImportPGN : undefined}
        />
      )}
      
      {/* Settings Panel */}
      <SettingsPanel
        isOpen={settingsPanelOpen}
        onClose={handleCloseSettings}
        soundsEnabled={soundsEnabled}
        onToggleSound={handleToggleSound}
        soundVolume={soundVolume}
        onVolumeChange={(volume) => {
          setSoundVolume(volume);
          soundManager.setVolume(volume);
        }}
        flipped={flipped}
        onFlipBoard={handleFlipBoard}
        uiTheme={uiTheme}
        onUIThemeChange={handleUIThemeChange}
        boardTheme={boardTheme}
        onBoardThemeChange={handleBoardThemeChange}
        pieceSet={pieceSet}
        onPieceSetChange={handlePieceSetChange}
      />
    </div>
  );
}

export default App;
